import { useEffect, useState } from "react";
import axios from "../api/axios";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { Link, useNavigate } from "react-router-dom";

import TaskManager from "./TaskManager";

/* ============================================
   FORMATEADOR NUMÉRICO
============================================ */
const safeNumber = (value) => {
  const num = Number(value);
  if (isNaN(num)) return "0,00";

  return num.toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

/* ============================================
   DASHBOARD PRO
============================================ */
export default function DashboardPro({ unreadMail = 0 }) {

  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [ventas, setVentas] = useState([]);
  const [compras, setCompras] = useState([]);
  const [cobros, setCobros] = useState([]);
  const [factVencidas, setFactVencidas] = useState(0);
  
  const [comprasPendientes, setComprasPendientes] = useState(0);

  useEffect(() => {
    cargarDatos();
    cargarAlertasSidebar();
  }, []);

  const cargarDatos = async () => {
    try {
      const [kp, v, c, cob] = await Promise.all([
        axios.get("/dashboard/kpis"),
        axios.get("/dashboard/ventas-mensuales"),
        axios.get("/dashboard/compras-mensuales"),
        axios.get("/dashboard/cobros-mensuales")
      ]);

      setKpis(kp.data);
      setVentas(v.data);
      setCompras(c.data);
      setCobros(cob.data);
    } catch (err) {
      console.log("Error Dashboard Pro:", err);
    }
  };

  const cargarAlertasSidebar = async () => {
    try {
      const r = await axios.get("/compras");
      const lista = r.data || [];

      let vencidas = 0;
      let pendientes = 0;
      const hoy = new Date();

      for (const c of lista) {
        const fecha = c.fechaVencimiento ? new Date(c.fechaVencimiento) : null;

        if (fecha && fecha < hoy && c.estado !== "pagada") vencidas++;
        if (c.estado !== "pagada") pendientes++;
      }

      setFactVencidas(vencidas);
      setComprasPendientes(pendientes);
    } catch (err) {
      console.log("Error Sidebar:", err);
    }
  };

  if (!kpis) return <p style={{ padding: 20 }}>Cargando estadísticas…</p>;

  /* ============================================
      UI
  ============================================= */
  return (
<div
  style={{
    marginTop: 10,
    borderRadius: 18,
    padding: 16,
    minHeight: "calc(100vh - 90px)",
    display: "flex",
    flexDirection: "column",
    flex: 1,                // 👈 CLAVE
    background:
      "radial-gradient(circle at top left, #1f2937, #020617 60%, #000000)",
    color: "white",
    boxShadow: "0 18px 35px rgba(0,0,0,0.45)"
  }}
>


<div
  style={{
    display: "flex",
    flexDirection: "column",
    gap: 14,
    flex: 1              // 👈 CLAVE
  }}
>



        {/* =====================================
            IZQUIERDA (Módulos + KPIs + Gráficas + Agenda)
        ===================================== */}
        <div>
          {/* BOTONES SUPERIORES */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(10, minmax(0, 1fr))",
              gap: 8,
              marginBottom: 14
            }}
          >
            <BotonModulo to="/clientes" icon="👥" text="Clientes" />
            <BotonModulo to="/maquinas" icon="🏭" text="Máquinas" />
            <BotonModulo to="/presupuestos" icon="📋" text="Presupuestos" />
            <BotonModulo to="/facturas" icon="🧾" text="Facturas" />
            <BotonModulo to="/compras" icon="💶" text="Compras" />
            <BotonModulo to="/ajustes" icon="⚙️" text="Ajustes" />
            <BotonModulo to="/tecnicos" icon="🛠️" text="Técnicos" />
            <BotonModulo to="/correo" icon="📨" text="Correo" />
            <BotonModulo to="/bancos" icon="🏦" text="Bancos" />
            <BotonModulo to="/crm" icon="📌" text="CRM" />

          </div>

          

          {/* =======================
              KPIs — 7 BLOQUES
          ======================= */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
              gap: 10,
              marginBottom: 12
            }}
          >
            <KpiCard
              title="Total vendido"
              value={safeNumber(kpis.totalVendido)}
              sub={safeNumber(kpis.totalVendidoSinIva)}
              accent="#3b82f6"
            />

            <KpiCard
              title="Cobrado"
              value={safeNumber(kpis.totalCobrado)}
              sub={safeNumber(kpis.totalCobradoSinIva)}
              accent="#22c55e"
            />

            <KpiCard
              title="Pendiente"
              value={safeNumber(kpis.pendienteCobro)}
              sub={safeNumber(kpis.pendienteCobroSinIva)}
              accent="#eab308"
            />

            <KpiCard
              title="Compras"
              value={safeNumber(kpis.totalCompras)}
              sub={safeNumber(kpis.totalComprasSinIva)}
              accent="#a855f7"
            />

            <KpiCard
              title="Pendiente compras"
              value={safeNumber(kpis.pendienteCompras)}
              sub={safeNumber(kpis.pendienteComprasSinIva)}
              accent="#f97316"
            />

            <KpiCard
              title="Beneficio actual"
              value={safeNumber(kpis.beneficioActual)}
              sub={safeNumber(kpis.beneficioActualSinIva)}
              accent={
                Number(kpis.beneficioActual) >= 0 ? "#22c55e" : "#ef4444"
              }
            />

            <KpiCard
              title="Beneficio futuro"
              value={safeNumber(kpis.beneficioFuturo)}
              sub={safeNumber(kpis.beneficioFuturoSinIva)}
              accent={
                Number(kpis.beneficioFuturo) >= 0 ? "#22c55e" : "#ef4444"
              }
            />
          </div>

          {/* =======================
              GRÁFICAS PEQUEÑAS: VENTAS / COMPRAS
          ======================= */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginBottom: 12
            }}
          >
            <Card title="Ventas mensuales">
              <ResponsiveContainer width="100%" height={120}>
                <LineChart data={ventas}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(148,163,184,0.4)"
                  />
                  <XAxis dataKey="mes" stroke="#9ca3af" fontSize={10} />
                  <YAxis stroke="#9ca3af" fontSize={10} />
                  <Tooltip
                    contentStyle={{
                      background: "#020617",
                      border: "1px solid #4b5563",
                      borderRadius: 8,
                      fontSize: 11
                    }}
                  />
                  <Line
                    dataKey="total"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={false}
                    type="monotone"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card>

            <Card title="Compras mensuales">
              <ResponsiveContainer width="100%" height={120}>
                <BarChart data={compras}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(148,163,184,0.4)"
                  />
                  <XAxis dataKey="mes" stroke="#9ca3af" fontSize={10} />
                  <YAxis stroke="#9ca3af" fontSize={10} />
                  <Tooltip
                    contentStyle={{
                      background: "#020617",
                      border: "1px solid #4b5563",
                      borderRadius: 8,
                      fontSize: 11
                    }}
                  />
                  <Bar dataKey="total" fill="#a855f7" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>
{/* =======================
    ALERTAS / INFO RÁPIDA
======================= */}
<div
  style={{
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 10,
    marginBottom: 14
  }}
>

  <SidebarCard title="Facturas vencidas" icon="🔥" accent="#ef4444">
    <SidebarNumber value={factVencidas} label="por pagar" />
  </SidebarCard>

  <SidebarCard title="Compras pendientes" icon="⚠️" accent="#eab308">
    <SidebarNumber value={comprasPendientes} label="sin pagar" />
  </SidebarCard>

  <SidebarCard
    title="Correo"
    icon="📨"
    accent={unreadMail > 0 ? "#22c55e" : "#3b82f6"}
    onClick={() => navigate("/correo")}
    clickable
  >
    {unreadMail > 0 ? (
      <SidebarNumber
        value={unreadMail}
        label={`correo${unreadMail > 1 ? "s" : ""} nuevo${unreadMail > 1 ? "s" : ""}`}
      />
    ) : (
      <SidebarText value="Sin correos nuevos" />
    )}
  </SidebarCard>
</div>



          {/* =======================
              AGENDA GRANDE
          ======================= */}
          <Card title="Agenda / Tareas" style={{ flex: 1 }}>
  <div
    style={{
      height: "100%",
      overflowY: "auto",
      paddingRight: 4
    }}
  >
    <TaskManager />
  </div>
</Card>

        </div>

        
      </div>
    </div>
  );
}

/* ============================================================
   COMPONENTES DE UI
============================================================ */

function glassStyle(extra = {}) {
  return {
    background: "rgba(15,23,42,0.78)",
    borderRadius: 14,
    border: "1px solid rgba(148,163,184,0.45)",
    backdropFilter: "blur(12px)",
    boxShadow: "0 10px 25px rgba(0,0,0,0.45)",
    ...extra
  };
}

/* BOTÓN MÓDULO */
function BotonModulo({ to, icon, text }) {
  return (
    <Link
      to={to}
      style={glassStyle({
        padding: "8px 4px",
        textAlign: "center",
        textDecoration: "none",
        color: "white",
        fontSize: 11,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
        transition: "0.15s"
      })}
      onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
      onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
    >
      <div style={{ fontSize: 16 }}>{icon}</div>
      <div style={{ fontWeight: 600 }}>{text}</div>
    </Link>
  );
}

/* KPI */
function KpiCard({ title, value, sub, accent }) {
  return (
    <div
      style={glassStyle({
        padding: "10px 12px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        minHeight: 70,
        borderLeft: `3px solid ${accent}`
      })}
    >
      <div style={{ fontSize: 11, opacity: 0.75 }}>{title}</div>
      <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>
        {value} €
      </div>
      <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>
        Sin IVA: {sub} €
      </div>
    </div>
  );
}

/* TARJETA */
function Card({ title, children }) {
  return (
    <div style={glassStyle({ padding: 10, minHeight: 80 })}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          marginBottom: 6,
          opacity: 0.9
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

/* SIDEBAR CARDS */
function SidebarCard({ title, icon, accent, children, onClick, clickable }) {
  return (
    <div
      onClick={clickable ? onClick : undefined}
      style={glassStyle({
        padding: 10,
        borderLeft: `3px solid ${accent}`,
        minHeight: 70,
        cursor: clickable ? "pointer" : "default"
      })}
    >

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{title}</span>
      </div>
      <div style={{ marginTop: 6 }}>{children}</div>
    </div>
  );
}

function SidebarNumber({ value, label }) {
  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 700 }}>{value}</div>
      <div style={{ fontSize: 11, opacity: 0.7 }}>{label}</div>
    </div>
  );
}

function SidebarText({ value }) {
  return <div style={{ fontSize: 14, fontWeight: 600 }}>{value}</div>;
}
